import React, { useRef } from "react";
import { Icon } from "@iconify/react";
import { createEmptyCarouselCard } from "../../../utils/whatsappTemplateBuilder";

function InfoBox({ children }) {
  return (
    <div className="wa-tpl-info-box">
      <Icon icon="solar:info-circle-linear" width={18} />
      <ul>{children}</ul>
    </div>
  );
}

function CarouselCardEditor({
  card,
  index,
  buttonType,
  onChange,
  onRemove,
  canRemove,
}) {
  const fileRef = useRef(null);

  const updateCard = (patch) => onChange({ ...card, ...patch });

  const addButton = () => {
    if ((card.buttons || []).length >= 2) return;
    const empty =
      buttonType === "PHONE"
        ? { text: "", phone: "" }
        : buttonType === "URL"
          ? { text: "", url: "" }
          : { text: "" };
    updateCard({ buttons: [...(card.buttons || []), empty] });
  };

  return (
    <div className="wa-tpl-carousel-editor-card">
      <div className="wa-tpl-carousel-editor-head">
        <strong>Card {index + 1}</strong>
        {canRemove ? (
          <button type="button" className="wa-tpl-icon-btn" onClick={onRemove}>
            <Icon icon="solar:trash-bin-trash-linear" width={18} />
          </button>
        ) : null}
      </div>

      <div className="wa-tpl-field">
        <span>Card Image *</span>
        <div className="wa-tpl-upload-row">
          <button
            type="button"
            className="wa-tpl-btn-outline wa-tpl-upload-btn"
            onClick={() => fileRef.current?.click()}
          >
            <Icon icon="solar:upload-linear" width={16} />
            Upload Image
          </button>
          {card.imageFile ? (
            <span className="wa-tpl-uploaded-chip">
              <Icon icon="solar:check-circle-linear" width={16} />
              Uploaded
              <button
                type="button"
                className="wa-tpl-icon-btn"
                onClick={() => updateCard({ imageFile: null })}
              >
                <Icon icon="solar:close-circle-linear" width={16} />
              </button>
            </span>
          ) : null}
          <input
            ref={fileRef}
            type="file"
            className="d-none"
            accept="image/jpeg,image/png"
            onChange={(e) => updateCard({ imageFile: e.target.files?.[0] || null })}
          />
        </div>
        {card.imageFile ? (
          <div className="wa-tpl-thumb-preview small">
            <img src={URL.createObjectURL(card.imageFile)} alt={`Card ${index + 1}`} />
          </div>
        ) : null}
      </div>

      <label className="wa-tpl-field">
        <span>Card Body Text *</span>
        <textarea
          className="wa-tpl-textarea"
          rows={3}
          maxLength={160}
          value={card.bodyText}
          onChange={(e) => updateCard({ bodyText: e.target.value })}
          placeholder="Card text..."
        />
        <small>{(card.bodyText || "").length}/160</small>
      </label>

      <div className="wa-tpl-buttons-section">
        <div className="wa-tpl-section-title">
          Buttons (Max 2)
          {(card.buttons || []).length < 2 ? (
            <button type="button" className="wa-tpl-link-btn" onClick={addButton}>
              + Add Button
            </button>
          ) : null}
        </div>
        {(card.buttons || []).map((btn, bi) => (
          <div key={`btn-${bi}`} className="wa-tpl-button-row">
            <input
              type="text"
              maxLength={20}
              placeholder="Button text..."
              value={btn.text}
              onChange={(e) => {
                const buttons = [...(card.buttons || [])];
                buttons[bi] = { ...buttons[bi], text: e.target.value };
                updateCard({ buttons });
              }}
            />
            {buttonType === "PHONE" ? (
              <input
                type="text"
                placeholder="+1234567890"
                value={btn.phone || ""}
                onChange={(e) => {
                  const buttons = [...(card.buttons || [])];
                  buttons[bi] = { ...buttons[bi], phone: e.target.value };
                  updateCard({ buttons });
                }}
              />
            ) : null}
            {buttonType === "URL" ? (
              <input
                type="url"
                placeholder="https://example.com"
                value={btn.url || ""}
                onChange={(e) => {
                  const buttons = [...(card.buttons || [])];
                  buttons[bi] = { ...buttons[bi], url: e.target.value };
                  updateCard({ buttons });
                }}
              />
            ) : null}
            <button
              type="button"
              className="wa-tpl-icon-btn"
              onClick={() =>
                updateCard({
                  buttons: (card.buttons || []).filter((_, i) => i !== bi),
                })
              }
            >
              <Icon icon="solar:close-circle-linear" width={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TemplateCarouselStep({ form, updateForm }) {
  const cards = form.carouselCards || [];

  const updateCard = (index, nextCard) => {
    const next = [...cards];
    next[index] = nextCard;
    updateForm({ carouselCards: next });
  };

  const addCard = () => {
    if (cards.length >= 10) return;
    updateForm({ carouselCards: [...cards, createEmptyCarouselCard()] });
  };

  const removeCard = (index) => {
    if (cards.length <= 2) return;
    updateForm({ carouselCards: cards.filter((_, i) => i !== index) });
  };

  return (
    <>
      <label className="wa-tpl-field">
        <span>Message body (shown above cards) *</span>
        <textarea
          className="wa-tpl-textarea"
          rows={4}
          maxLength={1024}
          value={form.bodyText}
          onChange={(e) => updateForm({ bodyText: e.target.value })}
          placeholder="Hello..."
        />
        <small>{form.bodyText.length}/1024</small>
      </label>

      <div className="wa-tpl-carousel-toolbar">
        <div className="wa-tpl-section-title">
          Cards ({cards.length}/10 — min 2)
        </div>
        {cards.length < 10 ? (
          <button type="button" className="wa-tpl-link-btn" onClick={addCard}>
            + Add Card
          </button>
        ) : null}
      </div>

      <div className="wa-tpl-radio-row">
        {[
          { value: "QUICK_REPLY", label: "Quick Reply" },
          { value: "URL", label: "URL" },
          { value: "PHONE", label: "Phone" },
        ].map((opt) => (
          <label key={opt.value} className="wa-tpl-radio-pill">
            <input
              type="radio"
              name="carouselButtonType"
              checked={form.carouselButtonType === opt.value}
              onChange={() => updateForm({ carouselButtonType: opt.value })}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>

      <div className="wa-tpl-carousel-editor-list">
        {cards.map((card, index) => (
          <CarouselCardEditor
            key={card.id}
            card={card}
            index={index}
            buttonType={form.carouselButtonType}
            canRemove={cards.length > 2}
            onChange={(next) => updateCard(index, next)}
            onRemove={() => removeCard(index)}
          />
        ))}
      </div>

      <InfoBox>
        <li>Minimum 2 cards, maximum 10</li>
        <li>Each card needs a JPEG/PNG image (max 5MB)</li>
        <li>Card body max 160 characters</li>
        <li>All cards must have the same button types</li>
      </InfoBox>
    </>
  );
}
